import Section, { SectionVisibilityProps } from './Section';
import { useTranslation } from 'react-i18next';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import CheckmarkTextInput from '../../inputs/CheckmarkTextInput';
import collect from 'collect.js';
import PhoneNumberInput from '../../PhoneNumberInput';
import { SectionType } from './CheckoutInfoBag';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useInitialData from '../../../hooks/global/useInitialData';

export interface FieldProps {
  value: string;
  onChange: (value: string) => void;
  validationErrors?: string[];
  className?: string;
}

const EMAIL_REGEX: RegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const isFieldValid = (value: string, extraChecks?: ((value: string) => boolean)[]) =>
  value?.length > 0 && collect(extraChecks).every((callback) => callback(value));

export const FirstNameField = ({ value, onChange, className, ...props }: FieldProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  return (
    <CheckmarkTextInput
      type="text"
      value={value}
      onChange={onChange}
      isComplete={isFieldValid(value)}
      placeholder={t('form.customer_first_name.placeholder')}
      classes={{
        container: className,
      }}
      {...props}
    />
  );
};

export const LastNameField = ({ value, onChange, className, ...props }: FieldProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  return (
    <CheckmarkTextInput
      type="text"
      value={value}
      onChange={onChange}
      isComplete={isFieldValid(value)}
      placeholder={t('form.customer_last_name.placeholder')}
      classes={{
        container: className,
      }}
      {...props}
    />
  );
};

export const EmailField = ({ value, onChange, className, ...props }: FieldProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  return (
    <CheckmarkTextInput
      type="email"
      value={value}
      onChange={onChange}
      isComplete={isFieldValid(value, [(value: string) => EMAIL_REGEX.test(value.toLocaleLowerCase())])}
      placeholder={t('form.customer_email.placeholder')}
      classes={{
        container: className,
      }}
      {...props}
    />
  );
};

export default function CustomerInfoSection({ show }: SectionVisibilityProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:Sections:CustomerInfoSection' });

  const { localData, update, validationErrors } = useOrderFlow();
  const { flattenValidationErrors } = useAppMethods();
  const { data: initialData } = useInitialData();

  return (
    <Section
      hideDivider
      show={show}
      id={SectionType.customer_info}
      title={t('title')}
      className="bordered p-md"
      containerClassName="lg:px-none px-md pb-md"
    >
      <div className="grid grid-cols-2 gap-2">
        <FirstNameField
          value={localData.customer_first_name}
          onChange={(value: string) => update('customer_first_name', value)}
          validationErrors={flattenValidationErrors('customer_first_name', validationErrors, t)}
          className="lg:col-span-1 col-span-2"
        />
        <LastNameField
          value={localData.customer_last_name}
          onChange={(value: string) => update('customer_last_name', value)}
          validationErrors={flattenValidationErrors('customer_last_name', validationErrors, t)}
          className="lg:col-span-1 col-span-2"
        />
        <EmailField
          value={localData.customer_email}
          onChange={(value: string) => update('customer_email', value)}
          validationErrors={flattenValidationErrors('customer_email', validationErrors, t)}
          className="col-span-2"
        />
        <PhoneNumberInput
          value={localData.customer_phone_number}
          placeholder={t('form.customer_phone_number.placeholder')}
          className="col-span-2"
          onChange={(value) => update('customer_phone_number', value)}
          validationErrors={flattenValidationErrors('customer_phone_number', validationErrors, t)}
          initialCountry={initialData.company?.country}
        />
      </div>
    </Section>
  );
}
